import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Send, Users, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SendNotificationPage() {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const [users, setUsers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [filterRole, setFilterRole] = useState('all');
  const [search, setSearch] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`, { withCredentials: true });
      // Lawyers can only see clients
      const filtered = user?.role === 'lawyer' ? res.data.filter(u => u.role === 'client') : res.data;
      setUsers(filtered);
    } catch {}
  }, [user?.role]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSend = async () => {
    if (!selectedIds.length || !title.trim() || !message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/notifications/send`, { user_ids: selectedIds, title, message }, { withCredentials: true });
      setSent(true);
      setSelectedIds([]);
      setTitle('');
      setMessage('');
      setTimeout(() => setSent(false), 3000);
    } catch {}
    setSending(false);
  };

  const toggleUser = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => {
    const visible = filteredUsers.map(u => u.user_id);
    setSelectedIds(prev => {
      const allSelected = visible.every(id => prev.includes(id));
      if (allSelected) return prev.filter(id => !visible.includes(id));
      return [...new Set([...prev, ...visible])];
    });
  };

  const filteredUsers = users.filter(u => {
    if (filterRole !== 'all' && u.role !== filterRole) return false;
    if (search && !u.name?.toLowerCase().includes(search.toLowerCase()) && !u.email?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <DashboardLayout>
      <div data-testid="send-notification-page">
        <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A] font-heading mb-6">
          {lang === 'ar' ? 'إرسال إشعار' : 'Send Notification'}
        </h1>

        {sent && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2" data-testid="notification-sent-success">
            <CheckCircle className="w-4 h-4" /> {lang === 'ar' ? 'تم إرسال الإشعار بنجاح' : 'Notification sent successfully!'}
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Message form */}
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-5 space-y-4">
              <div>
                <Label className="text-sm">{lang === 'ar' ? 'عنوان الإشعار' : 'Notification Title'}</Label>
                <Input value={title} onChange={e => setTitle(e.target.value)} className="mt-1" placeholder={lang === 'ar' ? 'عنوان الرسالة' : 'Enter title'} data-testid="notif-title-input" />
              </div>
              <div>
                <Label className="text-sm">{lang === 'ar' ? 'الرسالة' : 'Message'}</Label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 p-3 text-sm min-h-[100px] focus:ring-2 focus:ring-[#B48E4B]/50 focus:outline-none" placeholder={lang === 'ar' ? 'اكتب رسالتك...' : 'Write your message...'} data-testid="notif-message-input" />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-[#475569]">
                  {lang === 'ar' ? `تم اختيار ${selectedIds.length} مستخدم` : `${selectedIds.length} user(s) selected`}
                </span>
              </div>
              <Button onClick={handleSend} disabled={sending || !selectedIds.length || !title.trim()} className="w-full bg-[#B48E4B] text-white hover:bg-[#8C6A32]" data-testid="send-notification-btn">
                <Send className="w-4 h-4 me-2" /> {sending ? '...' : (lang === 'ar' ? 'إرسال الإشعار' : 'Send Notification')}
              </Button>
            </CardContent>
          </Card>

          {/* User selection */}
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Input placeholder={lang === 'ar' ? 'بحث...' : 'Search...'} value={search} onChange={e => setSearch(e.target.value)} className="flex-1 h-9" />
                {user?.role === 'admin' && (
                  <div className="flex bg-slate-100 rounded-md p-0.5 text-xs">
                    {['all', 'client', 'lawyer', 'admin'].map(r => (
                      <button key={r} onClick={() => setFilterRole(r)}
                        className={`px-2 py-1 rounded ${filterRole === r ? 'bg-white shadow-sm text-[#0F172A]' : 'text-[#475569]'}`}>
                        {r === 'all' ? (lang === 'ar' ? 'الكل' : 'All') : lang === 'ar' ? { client: 'عملاء', lawyer: 'محامين', admin: 'مدراء' }[r] : r}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button onClick={selectAll} className="text-xs text-[#B48E4B] mb-3 hover:underline" data-testid="select-all-users">
                {lang === 'ar' ? 'تحديد/إلغاء الكل' : 'Select/Deselect All'}
              </button>

              <div className="space-y-1 max-h-[350px] overflow-y-auto">
                {filteredUsers.map(u => (
                  <label key={u.user_id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${selectedIds.includes(u.user_id) ? 'bg-[#B48E4B]/5 border border-[#B48E4B]/20' : 'hover:bg-slate-50'}`} data-testid={`select-user-${u.user_id}`}>
                    <Checkbox checked={selectedIds.includes(u.user_id)} onCheckedChange={() => toggleUser(u.user_id)} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0F172A] truncate">{u.name}</p>
                      <p className="text-xs text-[#94A3B8] truncate">{u.email || u.phone}</p>
                    </div>
                    <span className={`px-1.5 py-0.5 text-[10px] rounded-full capitalize ${u.role === 'admin' ? 'bg-[#0F172A] text-white' : u.role === 'lawyer' ? 'bg-[#B48E4B]/10 text-[#B48E4B]' : 'bg-slate-100 text-[#475569]'}`}>{u.role}</span>
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
